import { useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { money } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Sparkles, Pencil, Trash2 } from "lucide-react";
import { GarmentForm, emptyGarment } from "./GarmentForm";
import { GarmentAIImport } from "./GarmentAIImport";

function Swatches({ colors = [] }) {
  return (
    <div className="flex flex-wrap gap-0.5 max-w-[220px]">
      {colors.slice(0, 18).map((c, i) => <span key={i} title={c.name} className="w-3.5 h-3.5 rounded-sm border border-slate-300" style={{ background: c.hex || "#fff" }} />)}
      {colors.length > 18 && <span className="text-[10px] text-slate-400 ml-1">+{colors.length - 18}</span>}
    </div>
  );
}

export function GarmentCatalog({ garments, reload }) {
  const [edit, setEdit] = useState(null);
  const [ai, setAi] = useState(false);

  const save = async () => {
    const body = { ...edit, cost_each: Number(edit.cost_each) || 0, retail_price: Number(edit.retail_price) || 0 };
    try {
      if (edit.id) await api.put(`/garments/${edit.id}`, body); else await api.post("/garments", body);
      toast.success("Garment saved"); setEdit(null); reload();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const del = async (g) => {
    if (!window.confirm(`Delete ${g.name}?`)) return;
    await api.delete(`/garments/${g.id}`); reload();
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="garment-catalog">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div><h3 className="font-head font-bold">Garment Catalogue</h3><p className="text-xs text-slate-500">Models, sizes and colours used in apparel quotes.</p></div>
        <div className="flex gap-2">
          <Button data-testid="garment-ai-open" onClick={() => setAi(true)} variant="outline" className="rounded-lg"><Sparkles size={15} className="mr-1.5 text-[#2495D3]" />Import from PDF / screenshot</Button>
          <Button data-testid="garment-add" onClick={() => setEdit(emptyGarment())} className="bg-[#2495D3] hover:bg-[#1E7AA9] rounded-lg"><Plus size={15} className="mr-1" />Add</Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-[10px] font-mono uppercase tracking-widest text-slate-400 border-b border-slate-100 text-left">
            <th className="py-2">Garment</th><th>Style #</th><th>Sizes</th><th>Colours</th><th className="text-right">Cost</th><th className="text-right">Sale</th><th />
          </tr></thead>
          <tbody>
            {garments.map((g) => (
              <tr key={g.id} className="border-b border-slate-50" data-testid={`garment-row-${g.id}`}>
                <td className="py-2"><div className="font-semibold">{g.name}{g.is_default && <span className="ml-2 text-[10px] font-mono uppercase bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Default</span>}</div><div className="text-[11px] text-slate-400">{[g.brand, g.category].filter(Boolean).join(" · ")}</div></td>
                <td className="font-mono text-xs">{g.style_number || "—"}</td>
                <td className="text-xs text-slate-600">{(g.sizes || []).length ? `${g.sizes[0]}–${g.sizes[g.sizes.length - 1]}` : "—"}</td>
                <td><Swatches colors={g.colors} /></td>
                <td className="text-right num">{money(g.cost_each)}</td>
                <td className="text-right num" data-testid={`garment-sale-${g.id}`}>{g.retail_price > 0 ? <>{money(g.retail_price)}<div className="text-[10px] text-emerald-600">+{money(g.retail_price - g.cost_each)}</div></> : <span className="text-slate-400 text-xs">markup</span>}</td>
                <td className="text-right whitespace-nowrap">
                  <button data-testid={`garment-edit-${g.id}`} onClick={() => setEdit({ ...g, sizes: g.sizes || [], colors: g.colors || [] })} className="p-1.5 text-slate-400 hover:text-[#2495D3]"><Pencil size={15} /></button>
                  <button data-testid={`garment-del-${g.id}`} onClick={() => del(g)} className="p-1.5 text-slate-400 hover:text-red-500"><Trash2 size={15} /></button>
                </td>
              </tr>
            ))}
            {!garments.length && <tr><td colSpan={7} className="py-8 text-center text-slate-400 text-xs">No garments yet — import a supplier catalogue.</td></tr>}
          </tbody>
        </table>
      </div>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="garment-dialog">
          <DialogHeader><DialogTitle>{edit?.id ? "Edit garment" : "New garment"}</DialogTitle></DialogHeader>
          {edit && <GarmentForm g={edit} onChange={setEdit} />}
          <Button data-testid="garment-save" onClick={save} className="bg-[#2495D3] hover:bg-[#1E7AA9] rounded-lg">Save</Button>
        </DialogContent>
      </Dialog>
      <GarmentAIImport open={ai} onOpenChange={setAi} onSaved={reload} />
    </div>
  );
}
